import {
  useDisclosure,
  MenuItem,
  MenuDivider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  HStack,
  Icon,
} from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router";
import ButtonWithConfirm from "components/Button/ButtonWithConfirm";
import { VThreeDotMenu } from "components/Menu/ThreeDotMenu";
import { Path } from "features/Path/models";
import { useDeleteV2Path } from "features/Path/hooks";
import { PEOPLE_TEAM_ROUTES } from "definitions/constants/routeConstants";
import CaminoDelete from "components/Icon/CaminoDelete";
import CaminoSettings from "components/Icon/CaminoSettings";
import CaminoDuplicate from "components/Icon/CaminoDuplicate";
import MenuItemIcon from "components/Icon/MenuItemIcon";
import { FiUserPlus } from "react-icons/fi";
import AddPathToNewHireForm from "../Form/AddPathToNewHireForm";
import { DuplicatePathForm, UpdatePathForm } from "../Form/PathForm";

interface PathMenuButtonProps {
  path: Path;
}

const DELETE_CONFIRM_BODY = "Any actions already scheduled or sent (meetings, emails, messages, tasks) will not be affected.";

function confirmDeleteProps(pathName: string) {
  return {
    header: `Delete "${pathName}"`,
    body: `"${pathName}" will be permanently deleted. ${DELETE_CONFIRM_BODY}`,
    confirmButtonText: "Yes, delete",
  };
}

export default function PathMenuButton({ path }: PathMenuButtonProps) {
  const { isOpen: detailsOpen, onOpen: openDetails, onClose: closeDetails } = useDisclosure();
  const { isOpen: duplicateOpen, onOpen: openDuplicate, onClose: closeDuplicate } = useDisclosure();
  const { isOpen: assignOpen, onOpen: openAssign, onClose: closeAssign } = useDisclosure();

  const navigate = useNavigate();
  const location = useLocation();
  const pathsListRoute = `${PEOPLE_TEAM_ROUTES.planning.index}/paths`;
  const isOnPathsIndex = location.pathname === pathsListRoute;
  const { mutate: handleDelete } = useDeleteV2Path(path?.id, () => {
    if (!isOnPathsIndex) navigate(pathsListRoute);
  });

  return (
    <>
      <VThreeDotMenu inPortal fontSize="xs" size="xs">
        <MenuItem icon={<MenuItemIcon icon={CaminoSettings} />} onClick={openDetails}>
          Edit Settings
        </MenuItem>

        {!path.autoAdd && (
          <MenuItem icon={<MenuItemIcon icon={FiUserPlus} />} onClick={openAssign}>
            Assign to new hire…
          </MenuItem>
        )}

        <MenuItem icon={<MenuItemIcon icon={CaminoDuplicate} />} onClick={openDuplicate}>
          Duplicate
        </MenuItem>

        <MenuDivider />

        <ButtonWithConfirm {...confirmDeleteProps(path.name)} intent="confirmation" handleConfirm={handleDelete}>
          <MenuItem icon={<MenuItemIcon icon={CaminoDelete} />} color="red.500">
            Delete
          </MenuItem>
        </ButtonWithConfirm>
      </VThreeDotMenu>

      <Modal closeOnOverlayClick={false} isOpen={detailsOpen} onClose={closeDetails} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize={30}>
            <HStack spacing={3}>
              <Icon as={CaminoSettings} boxSize={7} />
              <span>Edit Settings</span>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <UpdatePathForm path={path} onClose={closeDetails} />
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal closeOnOverlayClick={false} isOpen={duplicateOpen} onClose={closeDuplicate} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize={30}>Duplicate Path</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <DuplicatePathForm path={path} onClose={closeDuplicate} />
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal closeOnOverlayClick={false} isOpen={assignOpen} onClose={closeAssign} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize={24}>Assign path to new hire</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <AddPathToNewHireForm pathId={path.id} onClose={closeAssign} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
